import type { LegalDocument } from "../../../shared/legal";
import { LEGAL_PLACEHOLDER_NOTICE } from "../../../shared/legal";

/**
 * Generischer Renderer für ein rechtliches Dokument (AGB, Impressum,
 * Widerrufsbelehrung, Datenschutz) im Web. Spiegelt das Layout der
 * Datenschutz-Seite, damit alle rechtlichen Texte einheitlich aussehen.
 * Platzhalter-Abschnitte und vorformatierte Blöcke (z. B.
 * Muster-Widerrufsformular) werden deutlich hervorgehoben.
 */
export default function LegalDocumentView({
  document,
}: {
  document: LegalDocument;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid={`text-${document.id}-title`}>
          {document.title}
        </h1>
        <p className="mt-1 text-xs text-slate-500">{document.lastUpdated}</p>
      </div>

      <p className="text-sm text-slate-700 leading-relaxed">{document.intro}</p>

      <div className="space-y-5">
        {document.sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className={`card p-4 ${
              section.isPlaceholder ? "border-2 border-amber-300 bg-amber-50" : ""
            }`}
            data-testid={`section-${section.id}`}
          >
            <h2 className="text-base font-bold text-slate-900">{section.title}</h2>

            {section.paragraphs?.map((p, i) => (
              <p key={i} className="mt-2 text-sm text-slate-700 leading-relaxed">
                {p}
              </p>
            ))}

            {section.bullets && section.bullets.length > 0 ? (
              <ul className="mt-2 list-disc pl-5 space-y-1">
                {section.bullets.map((b, i) => (
                  <li key={i} className="text-sm text-slate-700 leading-relaxed">
                    {b}
                  </li>
                ))}
              </ul>
            ) : null}

            {section.preformatted ? (
              <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 font-mono leading-relaxed">
                {section.preformatted}
              </pre>
            ) : null}

            {section.isPlaceholder ? (
              <div className="mt-3 rounded-lg bg-white border border-amber-200 p-3 space-y-1.5">
                <p className="text-xs font-semibold text-amber-700">
                  {LEGAL_PLACEHOLDER_NOTICE}:
                </p>
                {section.placeholderFields?.map((row) => (
                  <div
                    key={row.label}
                    className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2"
                  >
                    <span className="text-xs font-medium text-slate-600 sm:w-40 shrink-0">
                      {row.label}
                    </span>
                    <span className="text-sm text-slate-800">{row.value}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        ))}
      </div>
    </div>
  );
}
